import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useSolPrice } from "@/hooks/useSolPrice";
import { toast } from "sonner";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionError";
import {
  Gamepad2,
  Loader2,
  RefreshCw,
  AlertCircle,
  Copy,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20 * 1000;

const ADMIN_WALLET = "ADJkonbgxndjbyL5XHfBLkVhEGiTUBChK9YMg8PtmPKa";
const UNLOCK_USD = 1;
export const UNLOCK_SI_REOPEN_KEY = "flow_unlock_si_reopen";

interface UnlockSpaceInvadersModalProps {
  open: boolean;
  onClose: () => void;
  playerId: string;
  onSuccess: () => void;
}

export function UnlockSpaceInvadersModal({ open, onClose, playerId, onSuccess }: UnlockSpaceInvadersModalProps) {
  const [step, setStep] = useState<"intro" | "pay">("intro");
  const [lockedQuote, setLockedQuote] = useState<null | { solPrice: number; solAmount: number; capturedAt: number }>(null);
  const [txSignature, setTxSignature] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const pollDeadlineRef = useRef<number>(0);
  const { price: solPrice, isLoading: priceLoading, refresh: refreshPrice, error: priceError } = useSolPrice();

  const liveSolNeeded = solPrice > 0 ? UNLOCK_USD / solPrice : 0;
  const quotedSolNeeded = lockedQuote?.solAmount ?? liveSolNeeded;
  const solDisplay = quotedSolNeeded > 0 ? quotedSolNeeded.toFixed(6) : "—";

  useEffect(() => {
    if (open) {
      setStep("intro");
      setLockedQuote(null);
      setTxSignature("");
      setShowManualFallback(false);
      setDetecting(false);
    }
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [open]);

  const copy = async (text: string, which: "wallet" | "amount") => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "wallet") {
        setCopiedWallet(true);
        setTimeout(() => setCopiedWallet(false), 1500);
      } else {
        setCopiedAmount(true);
        setTimeout(() => setCopiedAmount(false), 1500);
      }
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleProceed = () => {
    if (liveSolNeeded <= 0) {
      toast.error("SOL price unavailable. Refresh and try again.");
      return;
    }
    setLockedQuote({
      solPrice,
      solAmount: liveSolNeeded,
      capturedAt: Date.now(),
    });
    setStep("pay");
  };

  const handleVerify = async () => {
    const sig = txSignature.trim();
    if (!sig || sig.length < 40) {
      toast.error("Paste a valid transaction signature");
      return;
    }
    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("unlock-space-invaders", {
        body: {
          tx_signature: sig,
          player_id: playerId,
          quoted_sol_price: lockedQuote?.solPrice ?? solPrice,
        },
      });
      if (error) {
        throw new Error(await getSupabaseFunctionErrorMessage(error, "Verification failed"));
      }
      const res = data as any;
      if (res?.success) {
        toast.success(
          res.already_unlocked
            ? "Space Invaders already unlocked ✅"
            : "Space Invaders unlocked ✅ Enjoy 2 plays per day!",
        );
        onSuccess();
        onClose();
      } else {
        toast.error(res?.error || "Verification failed");
      }
    } catch (e: any) {
      toast.error(e?.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setDetecting(false);
  };

  const pollOnce = async (sinceTs: number) => {
    try {
      const { data, error } = await supabase.functions.invoke("auto-detect-space-invaders-unlock", {
        body: {
          player_id: playerId,
          quoted_sol_price: lockedQuote?.solPrice ?? solPrice,
          since_ts: sinceTs,
        },
      });
      if (!error) {
        const res = data as any;
        if (res?.status === "unlocked") {
          stopPolling();
          toast.success(
            res.already_unlocked
              ? "Space Invaders already unlocked ✅"
              : "Space Invaders unlocked ✅ Enjoy 2 plays per day!",
          );
          onSuccess();
          onClose();
          return;
        }
      }
    } catch (e) {
      console.warn("auto-detect-si exception:", e);
    }

    if (Date.now() < pollDeadlineRef.current) {
      pollTimerRef.current = window.setTimeout(() => pollOnce(sinceTs), POLL_INTERVAL_MS);
    } else {
      stopPolling();
      setShowManualFallback(true);
      toast.error("We couldn't detect your payment automatically. Please paste your transaction signature below.");
    }
  };

  const handleIvePaid = () => {
    if (detecting) return;
    const startedAt = Math.floor(Date.now() / 1000);
    setDetecting(true);
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    pollTimerRef.current = window.setTimeout(() => pollOnce(startedAt), 500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !verifying && onClose()}>
      <DialogContent className="max-w-sm bg-gray-900 border border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Gamepad2 className="w-5 h-5 text-pink-400" />
            Unlock Space Invaders
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            {step === "intro"
              ? "Pay $1 in SOL once and unlock the Space Invaders mini-game forever. 2 plays per day to earn virtual cash."
              : "Send the EXACT SOL amount below from any wallet, then tap \"I've paid\" — we'll detect it automatically."}
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <div className="space-y-4">
            <div className="rounded-xl border-2 border-pink-500/40 bg-gradient-to-br from-purple-500/10 to-pink-500/10 p-4 text-center">
              <Gamepad2 className="w-10 h-10 mx-auto text-pink-400 mb-2" style={{ filter: 'drop-shadow(0 0 10px rgba(236, 72, 153, 0.6))' }} />
              <p className="text-lg font-extrabold text-white">One-time unlock</p>
              <p className="text-3xl font-extrabold text-cyan-400 mt-1" style={{ textShadow: '0 0 12px rgba(34, 211, 238, 0.6)' }}>$1</p>
              <p className="text-xs text-gray-400 mt-2">Paid in SOL · Unlocked forever · 2 plays/day</p>
            </div>

            <div className="bg-black/60 rounded-lg p-3 border border-white/5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">SOL price:</span>
                <span className="text-white font-semibold flex items-center gap-1">
                  {priceLoading ? <Loader2 className="w-3 h-3 animate-spin" />
                    : priceError ? <span className="text-red-400">Error</span>
                    : `$${solPrice.toFixed(2)}`}
                  <button onClick={refreshPrice} className="text-gray-500 hover:text-white"><RefreshCw className="w-3 h-3" /></button>
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">You pay:</span>
                <span className="text-cyan-400 font-bold">{solDisplay} SOL</span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/20 text-gray-300 hover:bg-white/10">
                Cancel
              </Button>
              <Button
                onClick={handleProceed}
                disabled={liveSolNeeded <= 0 || priceLoading || !!priceError}
                className="font-bold bg-gradient-to-r from-pink-500 to-cyan-500 text-white hover:opacity-90 border-0"
              >
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "pay" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-100 flex gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <span>
                Open your Solana wallet (Phantom, Solflare, Backpack, exchange, etc.) and send the EXACT
                amount of SOL to the address below. Then tap "I've paid".
              </span>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wide text-gray-400">Send to (treasury wallet)</label>
              <div className="flex items-center gap-2 bg-black/60 rounded-lg p-2 border border-white/10">
                <code className="flex-1 text-[11px] text-cyan-300 break-all">{ADMIN_WALLET}</code>
                <button
                  onClick={() => copy(ADMIN_WALLET, "wallet")}
                  className="text-gray-400 hover:text-white p-1"
                  aria-label="Copy wallet"
                >
                  {copiedWallet ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wide text-gray-400">Amount to send</label>
              <div className="flex items-center gap-2 bg-black/60 rounded-lg p-2 border border-white/10">
                <code className="flex-1 text-sm text-yellow-300 font-bold">{solDisplay} SOL</code>
                <span className="text-[11px] text-gray-500">($1)</span>
                <button
                  onClick={() => copy(solDisplay, "amount")}
                  className="text-gray-400 hover:text-white p-1"
                  aria-label="Copy amount"
                >
                  {copiedAmount ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-gray-500">
                Tolerance ±0.005 SOL or ±$0.50.
              </p>
              {lockedQuote && (
                <p className="text-[10px] text-cyan-400">
                  Rate locked at ${lockedQuote.solPrice.toFixed(2)} per SOL for this payment.
                </p>
              )}
            </div>

            {detecting && (
              <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 p-3 text-xs text-white flex gap-2">
                <Loader2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0 animate-spin" />
                <div className="space-y-1 flex-1">
                  <p className="font-semibold">Checking payment… (max 20s)</p>
                  <p className="text-gray-300">
                    If you haven't sent the transfer yet, tap Stop and send it first.
                  </p>
                  <button
                    onClick={stopPolling}
                    className="mt-1 text-cyan-300 underline hover:text-white"
                  >
                    Stop detection
                  </button>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  stopPolling();
                  setStep("intro");
                  setLockedQuote(null);
                }}
                disabled={verifying}
                className="border-white/20 text-gray-300 hover:bg-white/10"
              >
                Back
              </Button>
              <Button
                onClick={handleIvePaid}
                disabled={detecting || verifying}
                className="font-bold bg-gradient-to-r from-pink-500 to-cyan-500 text-white hover:opacity-90 border-0"
              >
                {detecting ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Detecting…</>
                ) : (
                  <><Gamepad2 className="w-4 h-4 mr-1" /> I've paid</>
                )}
              </Button>
            </DialogFooter>

            <div className="mt-2 pt-3 border-t border-dashed border-white/10 opacity-70">
              <button
                type="button"
                onClick={() => setShowManualFallback((v) => !v)}
                className="w-full flex items-center justify-between text-[10px] text-gray-400 hover:text-white"
              >
                <span>Payment failed or not detected? Recover with TX signature</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showManualFallback ? "rotate-180" : ""}`} />
              </button>
              {showManualFallback && (
                <div className="space-y-2 mt-3">
                  <p className="text-[10px] text-gray-500">
                    Use this ONLY if your payment was sent but auto-detection failed.
                  </p>
                  <Input
                    value={txSignature}
                    onChange={(e) => setTxSignature(e.target.value)}
                    placeholder="Paste tx signature from your wallet"
                    className="bg-black/60 border-white/10 text-white text-xs"
                    disabled={verifying}
                  />
                  <Button
                    onClick={handleVerify}
                    disabled={verifying || !txSignature.trim()}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                  >
                    {verifying ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Verifying...</>
                    ) : (
                      <>Recover payment manually</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
